import { Dropdown } from "antd";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  SET_ALERT,
  SET_WAIT_MESSAGE,
  SetAlertAction,
  SetWaitMessageAction
} from "../../actions/actionTypes";
import {
  CustomMixInfo,
  MainStore,
  StemFidelities,
  StemFidelity,
  VariationSubmission
} from "../../store";
import { IconButton } from "../Buttons/IconButton";
import { LoadModal } from "../LoadModal";
import { ResetModal } from "../ResetModal/ResetModal";
import { SaveModal } from "../SaveModal/SaveModal";
import { TrackInfoModal } from "../TrackInfoModal";
import { VariationModal } from "../VariationModal";
import { Container, MainMenu, MainMenuItem } from "./styles";
import { Hamburger } from "../../../assets/Hamburger";
import useModal from "../../../hooks/useModal";
import { AuthContext } from "../../../context/AuthProvider";
import { saveCustomMixAction } from "../../actions/actionCreators";
import { EditorContext } from "../../context/EditorContext/EditorProvider";
import { VersionControl } from "../VersionControl";
import { submitAcceptedVariation } from "../../api/WebAPI";
import { useLocation } from "react-router-dom";
import { isDownloadLimitError } from "../../../utils/downloadGate";
import { promptSignupGate } from "../../../components/SignupGatePrompt";
import { useApolloClient } from "@apollo/react-hooks";
import { CREATE_DOWNLOAD_MUTATION_WITHOUT_TEAM } from "../../../components/TrackCheckoutDetail/queries";
import { saveAs } from "file-saver";

export function MainMenuControls() {
  const dispatch = useDispatch();
  const theme = useTheme();

  const versionDrawer = useModal();

  const isRendering = useSelector<MainStore, boolean>(
    state => state.editorState.isRendering
  );
  const customMixInfo = useSelector<MainStore, CustomMixInfo>(
    state => state.project.customMixInfo
  );
  const variationSubmission = useSelector<MainStore, VariationSubmission>(
    state => state.project.variationSubmission
  );

  const SAVE_MIX_BEFORE_VARIATION_SUBMISSION =
    "Please save your custom mix before submitting a variation for review";

  const user = useContext(AuthContext);
  const editor = useContext(EditorContext);
  const apolloClient = useApolloClient();
  const location = useLocation();

  const saveModal = useModal();
  const loadModal = useModal();
  const resetModal = useModal();
  const trackInfoModal = useModal();
  const variationModal = useModal();
  // Ref used to indicate the save modal was opened to continue into the
  // variation submission flow. When true, after a successful save the
  // SaveModal will invoke the onSaved callback which will open the
  // Variation modal and reset this flag.
  const pendingVariationRef = useRef(false);

  const history = useHistory();

  const customMixSaved = useCallback(
    customMixJson => {
      if (
        customMixInfo.id !== customMixJson.id ||
        customMixInfo.version.versionNumber !==
          customMixJson.version.versionNumber
      ) {
        editor.requestCustomMixIdOpen(
          customMixJson.id,
          customMixJson.version.versionNumber
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customMixInfo]
  );

  const quickSave = useCallback(
    callback => {
      if (!user.isUserPrivate()) return;
      if (customMixInfo.id === null || customMixInfo.projectId === null) {
        // this mix hasn't been saved before, spawn the Save As modal for first time save workflow
        saveModal.open();
      } else {
        // we have enough information to quicksave a new version
        dispatch<SetWaitMessageAction>({
          type: SET_WAIT_MESSAGE,
          id: "SAVING_CUSTOM_MIX",
          role: "saving",
          message: "Saving Custom Mix..."
        });
        dispatch(
          saveCustomMixAction(
            customMixInfo.id,
            customMixInfo.projectId,
            user.user.id,
            user.user.variationEditor,
            customMixJson => {
              dispatch<SetWaitMessageAction>({
                type: SET_WAIT_MESSAGE,
                id: "SAVING_CUSTOM_MIX",
                role: "saving",
                message: null
              });
              if (callback) callback(customMixJson);
            }
          )
        );
      }
    },
    [customMixInfo, saveModal, user, dispatch]
  );

  const openVariationModal = useCallback(
    customMixJson => {
      variationModal.open();
    },
    [variationModal]
  );

  const handleSaveModalOnSaved = useCallback(
    customMixJson => {
      if (pendingVariationRef.current) {
        pendingVariationRef.current = false;
        variationModal.open();
      }
    },
    [variationModal]
  );

  /**
   * Encodes an S3 object key for use in URLs.
   * - Leaves '/' intact (for folder-like paths)
   * - Converts spaces to '+'
   * - Percent-encodes all other unsafe characters
   */
  const encodeS3Key = (key: string): string => {
    return key
      .split("/")
      .map(part =>
        encodeURIComponent(part)
          .replace(/%20/g, "+")
          .replace(
            /[!'()*]/g,
            c =>
              "%" +
              c
                .charCodeAt(0)
                .toString(16)
                .toUpperCase()
          )
      )
      .join("/");
  };

  const onMainMenuClick = ({ key }) => {
    switch (key) {
      case "download":
        {
          let stemFidelity: StemFidelity = StemFidelities.SD;
          const hasPaidAccess = user.hasPaidTrackAccess(editor.initialTrack);
          const userLoggedIn = user.isUserPrivate();
          const isUserAdmin = user.isUserAdmin();
          const isContentReviewer = user.isUserContentReviewer();
          // use HD stems if the user has paid access or is an admin or content reviewer
          // we don't need lossless stems here, only for rendering variations
          if (
            userLoggedIn &&
            (hasPaidAccess || isUserAdmin || isContentReviewer)
          )
            stemFidelity = StemFidelities.HD;

          editor.requestMixdown(
            stemFidelity,
            (blob, filename) => {
              // Record the download; if the anonymous gate rejects, prompt
              // signup instead of saving the mixdown.
              apolloClient
                .mutate({
                  mutation: CREATE_DOWNLOAD_MUTATION_WITHOUT_TEAM,
                  variables: {
                    user: user?.user?.id ?? null,
                    track: editor.initialTrack?.id,
                    details: filename || "editor mixdown"
                  }
                })
                .then(result => {
                  if (isDownloadLimitError(result)) {
                    editor.close();
                    promptSignupGate(history, location);
                  } else {
                    saveAs(blob, filename);
                  }
                })
                .catch(err => {
                  if (isDownloadLimitError(err)) {
                    editor.close();
                    promptSignupGate(history, location);
                  } else {
                    saveAs(blob, filename);
                  }
                });
              // Hide loading message
              dispatch<SetWaitMessageAction>({
                type: SET_WAIT_MESSAGE,
                id: "PERFORMING_MIXDOWN",
                role: "processing",
                message: null
              });
            },
            () => {
              dispatch<SetWaitMessageAction>({
                type: SET_WAIT_MESSAGE,
                id: "PERFORMING_MIXDOWN",
                role: "processing",
                message: "Rendering Custom Mix..."
              });
            }
          );
        }
        break;
      case "save":
        if (user.isUserPrivate()) quickSave(customMixSaved);
        break;
      case "saveAs":
        if (user.isUserPrivate()) saveModal.open();
        break;
      case "load":
        if (user.isUserPrivate()) loadModal.open();
        break;
      case "reset":
        resetModal.open();
        break;
      case "versions":
        versionDrawer.open();
        break;
      case "info":
        trackInfoModal.open();
        break;
      case "variation":
        if (customMixInfo.id === null || customMixInfo.projectId === null) {
          // this mix hasn't been saved before, spawn the Save As modal for first time
          dispatch<SetAlertAction>({
            type: SET_ALERT,
            id: "SAVE_BEFORE_SUBMIT",
            role: "error",
            title: "Save Your Custom Mix",
            message: SAVE_MIX_BEFORE_VARIATION_SUBMISSION
          });
          // mark that the save modal was opened as part of a variation
          // submission flow so that after saving we automatically open the
          // variation modal for the user
          pendingVariationRef.current = true;
          saveModal.open();
        } else {
          // perform a quick save then open the variation submission modal
          quickSave(openVariationModal);
        }
        break;
      case "accept-variation":
        editor.requestMixdown(
          StemFidelities.LOSSLESS, // always render lossless for accepting variations
          (blob, filename) => {
            saveAs(blob, filename);

            // upload the variation to the server
            submitAcceptedVariation(
              new File([blob], filename, { type: blob.type }),
              variationSubmission.id
            ).then(variationSubmissionResponse => {
              // Hide loading message
              dispatch<SetWaitMessageAction>({
                type: SET_WAIT_MESSAGE,
                id: "ACCEPTING_VARIATION",
                role: "processing",
                message: null
              });

              // route to the admin ingestion page
              const s3Url = encodeS3Key(variationSubmissionResponse.s3Url);
              const ingestionLink = `https://bluedotsessions.s3.us-west-2.amazonaws.com/${s3Url}`;
              localStorage.setItem("ingestionLink", ingestionLink);
              history.push(
                `/admin/upload?variationSubmission=${variationSubmission.id}`
              );
            });
          },
          () => {
            dispatch<SetWaitMessageAction>({
              type: SET_WAIT_MESSAGE,
              id: "ACCEPTING_VARIATION",
              role: "processing",
              message: "Forming Variation From Custom Mix..."
            });
          }
        );
        break;
    }
  };

  const handleKeyDown = useCallback(
    e => {
      if (["text", "number", "textarea"].includes(e.target.type)) return;
      if (!user.isUserPrivate()) return;

      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.keyCode === 83) {
        // ctrl + shift + s
        saveModal.open();
      } else if ((e.metaKey || e.ctrlKey) && e.keyCode === 83) {
        // ctrl + s
        quickSave(customMixSaved);
      }
    },
    [user, quickSave, saveModal, customMixSaved]
  );

  // create a mutable ref to handler that gets refreshed with up-to-date redux store every rerender
  // this is necessary because of useEffect dependencies & redux
  const handleKeyDownRef = useRef(handleKeyDown);
  useEffect(() => {
    handleKeyDownRef.current = handleKeyDown;
  }, [handleKeyDown]);

  useEffect(() => {
    const keyDownHandler = e => handleKeyDownRef.current(e);
    if (editor.drawerState.visible) {
      document.addEventListener("keydown", keyDownHandler);
    } else {
      document.removeEventListener("keydown", keyDownHandler);
    }
    return () => document.removeEventListener("keydown", keyDownHandler);
  }, [editor.drawerState.visible]);

  const mainMenu = (
    <MainMenu onClick={onMainMenuClick}>
      {!isRendering && <MainMenuItem key="download">Download Mix</MainMenuItem>}
      <MainMenuItem
        key="save"
        className={!user.isUserPrivate() ? "disabled" : ""}
      >
        Save
      </MainMenuItem>
      <MainMenuItem
        key="saveAs"
        className={!user.isUserPrivate() ? "disabled" : ""}
      >
        Save As
      </MainMenuItem>
      <MainMenuItem
        key="load"
        className={!user.isUserPrivate() ? "disabled" : ""}
      >
        Load
      </MainMenuItem>
      {editor.customMix !== null && (
        <MainMenuItem key="versions">Versions</MainMenuItem>
      )}
      <MainMenuItem key="reset">Reset</MainMenuItem>
      <MainMenuItem key="info">Track Info</MainMenuItem>
      {user.isUserPrivate() &&
        (user.user.admin || user.user.variationEditor) && (
          <MainMenuItem key="variation">Submit Variation</MainMenuItem>
        )}
      {variationSubmission &&
        variationSubmission.status === "Pending" &&
        user.isUserPrivate() &&
        (user.user.admin || user.isUserContentReviewer()) && (
          <MainMenuItem key="accept-variation">Accept Variation</MainMenuItem>
        )}
    </MainMenu>
  );

  return (
    <Container>
      <Dropdown
        overlay={mainMenu}
        placement="bottomRight"
        trigger={["click"]}
        overlayStyle={{ zIndex: theme.zIndex.editorDrawer + 100 }}
      >
        <IconButton svgWidth="16px" svgHeight="16px">
          <Hamburger />
        </IconButton>
      </Dropdown>
      <SaveModal modalState={saveModal} onSaved={handleSaveModalOnSaved} />
      <LoadModal loadModalState={loadModal} saveModalState={saveModal} />
      <ResetModal modalState={resetModal} />
      <TrackInfoModal modalState={trackInfoModal} />
      <VariationModal modalState={variationModal} />
      {editor.customMix !== null && (
        <VersionControl drawerState={versionDrawer} />
      )}
    </Container>
  );
}
